import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { useEffect, useState } from 'react';

import FormModal from '@/components/v2/molecules/admin/shared/FormModal';


import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { Guest, useGuestMutations, useOrigins } from '@/hooks/useGuests';

import {
  createGuestSchema,
  CreateGuestSchema,
} from '@/schemas/admin/AdminGuestsTab/createGuestSchema';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

interface GuestFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  guest: Guest | null;
  defaultName?: string;
  defaultOriginId?: string;
  onGuestCreated?: (guest: Guest) => void;
}

const GuestFormModal = ({
  isOpen,
  onClose,
  guest,
  defaultName,
  defaultOriginId,
  onGuestCreated,
}: GuestFormModalProps) => {
  const { data: origins = [] } = useOrigins();
  const { createGuest, updateGuest } = useGuestMutations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CreateGuestSchema>({
    resolver: zodResolver(createGuestSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      origin_id: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (guest) {
      form.reset({
        name: guest.name,
        email: guest.email || '',
        phone: guest.phone || '',
        origin_id: guest.origin_id || '',
      });
    } else {
      form.reset({
        name: defaultName || '',
        email: '',
        phone: '',
        origin_id: defaultOriginId || '',
      });
    }
  }, [guest, defaultName, defaultOriginId, form]);

  const onSubmit = async (data: CreateGuestSchema) => {
    setIsSubmitting(true);
    try {
      const cleanData = {
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        origin_id: data.origin_id || undefined,
      };

      if (guest) {
        await updateGuest.mutateAsync({ id: guest.id, ...cleanData });
      } else {
        const newGuest = await createGuest.mutateAsync(cleanData);
        onGuestCreated?.(newGuest);
      }

      onClose();
    } catch (error) {
      console.error('Error submitting guest form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={guest ? 'Editar Huésped' : 'Agregar Nuevo Huésped'}
      description={guest ? 'Modifica los datos del huésped' : 'Completa la información del nuevo huésped. Solo el nombre es requerido.'}
      form={form}
      onSubmit={onSubmit}
      isLoading={isSubmitting}
      submitLabel={guest ? 'Actualizar' : 'Crear Huésped'}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nombre *</FormLabel>
            <FormControl>
              <Input
                placeholder="Nombre completo del huésped"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Correo electrónico</FormLabel>
            <FormControl>
              <Input
                type="email"
                placeholder="correo@ejemplo.com"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Teléfono</FormLabel>
            <FormControl>
              <PhoneInput
                placeholder="+52 123 456 7890"
                value={field.value}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="origin_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Origen</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el origen del huésped" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {origins.map((origin) => (
                  <SelectItem key={origin.id} value={origin.id}>
                    {origin.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormModal>
  );
};

export default GuestFormModal;
